import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PageShell, ShellSection } from "./PageShell";
import { NEWS, RESEARCH } from "../data/content";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

export default function NewsPage() {
    usePageMeta(
        "News & Insights — Titan Haven Group LLC",
        "Press releases, company news, product developments, and published research from Titan Haven Group LLC.",
    );
    return (
        <PageShell
            testId="news-page"
            overline="News & Insights"
            title="Company news, product developments, and published research."
            lede="The public record of what Titan Haven Group LLC builds, operates, and publishes."
        >
            <ShellSection>
                <div className="border-t border-thg-border">
                    {NEWS.map((item, i) => (
                        <Reveal
                            key={item.slug}
                            delay={i * 0.04}
                            as="article"
                            className="border-b border-thg-border"
                            data-testid={`news-page-item-${i}`}
                        >
                            <Link
                                to={`/news/${item.slug}`}
                                className="group grid grid-cols-1 md:grid-cols-12 gap-3 py-8 items-start"
                            >
                                <p className="md:col-span-3 text-[10px] tracking-[0.2em] uppercase font-bold text-thg-red">
                                    {item.type}
                                    <span className="block mt-2 text-thg-tertiary font-semibold">{item.date}</span>
                                </p>
                                <div className="md:col-span-8">
                                    <h2 className="font-display text-xl md:text-2xl font-medium text-white group-hover:text-thg-secondary transition-colors duration-300">
                                        {item.title}
                                    </h2>
                                    <p className="mt-3 text-sm leading-relaxed text-thg-secondary max-w-2xl">{item.text}</p>
                                </div>
                                <div className="md:col-span-1 md:text-right">
                                    <ArrowRight size={16} className="inline text-thg-tertiary group-hover:text-white transition-colors duration-300" />
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Public record</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        The company&rsquo;s development in chronological order. This record accumulates over time —
                        nothing is manufactured to fill it.
                    </p>
                </Reveal>
                <div className="mt-10 border-l border-thg-border ml-2" data-testid="timeline">
                    {[...NEWS].reverse().map((item, i) => (
                        <Reveal key={item.slug} delay={i * 0.04} className="relative pl-8 pb-8 last:pb-0">
                            <span className="absolute left-[-5px] top-1.5 block w-2.5 h-2.5 bg-thg-red" aria-hidden="true" />
                            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-thg-tertiary">{item.date}</p>
                            <Link
                                to={`/news/${item.slug}`}
                                className="mt-1 block font-display text-lg font-medium text-white hover:text-thg-secondary transition-colors duration-300"
                            >
                                {item.title}
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">WarRoom Labs — published research</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        Selected research from THG&rsquo;s external publication. The full archive lives on the WarRoom
                        Labs Substack.
                    </p>
                </Reveal>
                <div className="mt-10 border-t border-thg-border">
                    {RESEARCH.map((r, i) => (
                        <Reveal key={r.title} delay={i * 0.05} className="border-b border-thg-border">
                            <a
                                href={r.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`news-research-${i}`}
                                className="group flex items-center justify-between gap-6 py-5"
                                aria-label={`${r.title} — opens published research in a new tab`}
                            >
                                <div>
                                    <h3 className="font-display text-lg font-medium text-white group-hover:text-thg-secondary transition-colors duration-300">
                                        {r.title}
                                    </h3>
                                    <p className="mt-1 text-xs tracking-[0.15em] uppercase text-thg-tertiary">{r.topic}</p>
                                </div>
                                <ArrowUpRight size={16} className="shrink-0 text-thg-tertiary group-hover:text-white transition-colors duration-300" />
                            </a>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.1}>
                    <a
                        href="https://warroomlabs.substack.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="news-substack-cta"
                        className="mt-8 inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase"
                    >
                        Visit WarRoom Labs <ArrowUpRight size={16} />
                    </a>
                </Reveal>
            </ShellSection>
        </PageShell>
    );
}
